import { useCallback, useEffect, useRef, useState } from 'react';

interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Refetch without flipping the surface back into its full loading state. */
  refresh: () => void;
}

/**
 * Run an async fetch on mount and whenever `deps` change, with optional polling.
 * Polling refetches silently (no loading flash) so live panels update in place.
 * `fn` is captured per-render via a ref, so it needn't be memoized by callers.
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
  opts: { intervalMs?: number } = {},
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(async (silent: boolean) => {
    if (!silent) setLoading(true);
    try {
      const d = await fnRef.current();
      setData(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    run(false);
  }, deps);

  useEffect(() => {
    if (!opts.intervalMs) return;
    const id = window.setInterval(() => run(true), opts.intervalMs);
    return () => window.clearInterval(id);
  }, [opts.intervalMs, run]);

  return { data, error, loading, refresh: () => run(true) };
}
